// Hex color helpers for tenant theming + contrast-guarded accent variants
export function hexToRgb(hex) {
  let h = (hex || '#EE722E').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex(r, g, b) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mix(hex, target, amount) {
  const a = hexToRgb(hex);
  return rgbToHex(a.r + (target.r - a.r) * amount, a.g + (target.g - a.g) * amount, a.b + (target.b - a.b) * amount);
}
export function darken(hex, amount) { return mix(hex, { r: 0, g: 0, b: 0 }, amount); }
export function lighten(hex, amount) { return mix(hex, { r: 255, g: 255, b: 255 }, amount); }

export function relLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const f = (v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

// Darken until the color is readable as text/icon on white
export function accentForText(hex) {
  let c = hex; let guard = 0;
  while (relLuminance(c) > 0.42 && guard < 10) { c = darken(c, 0.14); guard += 1; }
  return c;
}

// Darken until the color works both as a FILL on white and behind white text
// (large-text AA ≈ 3:1). Only very light brand colors (e.g. Amnesty yellow) are
// adjusted; already-dark brand colors (orange, green, red, blues) stay untouched.
export function accentForFill(hex) {
  let c = hex; let guard = 0;
  while (relLuminance(c) > 0.31 && guard < 12) { c = darken(c, 0.10); guard += 1; }
  return c;
}

export function tenantVars(primary) {
  const p = primary || '#EE722E';
  const { r, g, b } = hexToRgb(p);
  const lum = relLuminance(p);
  const fill = accentForFill(p);
  const text = accentForText(p);
  const onAccent = lum > 0.6 ? '#181818' : '#FFFFFF';
  const gradientTop = lighten(p, lum > 0.6 ? 0.06 : 0.16);
  const gradientBottom = lum > 0.6 ? darken(p, 0.06) : p;
  return {
    '--tenant-accent': p,
    '--tenant-accent-500': fill,
    '--tenant-accent-600': text,
    '--tenant-accent-100': lighten(p, 0.84),
    '--tenant-accent-050': lighten(p, 0.92),
    '--tenant-accent-bright': lighten(p, 0.12),
    // Teintes dérivées (comblent les niveaux utilisés dans l'UI mais non définis
    // auparavant : 200/300/400 clairs, 700/800/900 foncés & lisibles comme texte).
    '--tenant-accent-200': lighten(p, 0.68),
    '--tenant-accent-300': lighten(p, 0.50),
    '--tenant-accent-400': lighten(p, 0.26),
    '--tenant-accent-700': darken(text, 0.10),
    '--tenant-accent-800': darken(text, 0.22),
    '--tenant-accent-900': darken(text, 0.34),
    '--tenant-on-accent': '#FFFFFF',
    '--tenant-on-brand': onAccent,
    '--tenant-gradient': `linear-gradient(135deg, ${gradientTop} 0%, ${gradientBottom} 100%)`,
    '--tenant-shadow': `rgba(${r}, ${g}, ${b}, 0.30)`,
  };
}
